import { Injectable } from '@angular/core';
import { OrderBackendService } from '../../../services/order/order-backend.service';
import { map, take, tap } from 'rxjs/operators';
import { SessionQuery } from '../../session/session.query';
import { applyTransaction } from '@datorama/akita';
import { OrderListItemModel } from '../../../models/order/order-list-item.model';
import { OrderListQuery } from '../../order-list/order-list.query';
import { OrderListService } from '../../order-list/order-list.service';
import { TableColumn } from '../../../models/table-column';
import { OrderItemsStoreService } from '../../order-items-store-service';
import { LocalStateKeys } from '../../local-state-keys';
import { IItemsServiceInterface } from '../../../interfaces/items/items-service.interface';
import { LocalStoreConfigModel } from '../../../models/local-store-config.model';
import {
  OrderListParameters,
  TaskResult,
} from 'src/app/core/models/client-models';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OrderItemsService
  implements IItemsServiceInterface<OrderListItemModel>
{
  constructor(
    private orderService: OrderBackendService,
    private orderItemsStoreService: OrderItemsStoreService,
    private orderListQuery: OrderListQuery,
    private orderListService: OrderListService,
    private sessionQuery: SessionQuery
  ) {}

  filterValueChange(filterValue: string, storeKey: LocalStateKeys) {
    applyTransaction(() => {
      this.clearCache(storeKey);
      this.orderListService.filterValueChange(filterValue);
    });
    this.getNextPage(storeKey);
  }

  clearSearchFilterValue(storeConfig?: LocalStoreConfigModel): void {
    this.orderListService.clearFilterValue();
  }

  setFilterOptions(
    filterSelection: any,
    storeConfig?: LocalStoreConfigModel
  ): void {
    applyTransaction(() => {
      this.orderListService.setFilterOptions(filterSelection, storeConfig);
      this.clearCache(storeConfig.orderItems);
    });
    this.getNextPage(storeConfig.orderItems);
  }

  showSelectedColumns(columns: TableColumn[]): void {
    this.orderListService.showSelectedColumns(columns);
  }

  /**
   * Load order list from Server with paging and filter.
   */
  getNextPage(storeKey: LocalStateKeys) {
    const store = this.orderItemsStoreService.getOrCreateStore(storeKey);
    store.store.setLoading(true);
    // Get values for http params.
    let nextPage = 0;
    store.query.page$.pipe(take(1)).subscribe((page) => (nextPage = page + 1));
    this.setPage(nextPage, storeKey);
    let pageSize = 150;
    store.query.pageSize$.pipe(take(1)).subscribe((size) => (pageSize = size));
    let IdPerson = 0;
    this.sessionQuery.idPerson$
      .pipe(take(1))
      .subscribe((id) => (IdPerson = id));
    let filterValue: string;
    this.orderListQuery.filterValue$
      .pipe(take(1))
      .subscribe((value) => (filterValue = value));

    let orderParams: OrderListParameters = {
      pageNumber: nextPage,
      pageSize: pageSize,
      personID: IdPerson,
      filterValue: filterValue ? filterValue : '',
    } as OrderListParameters;

    orderParams = this.orderListService.setFilter(orderParams);

    this.orderService
      .getOrderList(orderParams)
      .pipe(tap(() => store.store.setLoading(false)))
      .subscribe({
        next: (data) => {
          applyTransaction(() => {
            store.store.upsertMany(data);
            store.store.setError(null);
            if (data.length == 0) {
              // if no results set page to previous.
              this.setPage(nextPage - 1, storeKey);
              // set last page indicator.
              store.store.update((state) => (state.lastPage = true));
            }
          });
        },
        error: (error) => store.store.setError(error.error),
      });
  }

  getById(id: number, storeKey: LocalStateKeys) {
    const storeItems = this.orderItemsStoreService.getOrCreateStore(storeKey);
    this.orderService
      .getOrderByID(id)
      .pipe(tap(() => storeItems.store.setLoading(false)))
      .subscribe({
        next: (data) => {
          applyTransaction(() => {
            storeItems.store.upsertMany([data]);
            storeItems.store.setError(null);
            this.setActive(data, {
              orderItems: storeKey,
            } as LocalStoreConfigModel);
          });
        },
        error: (error) => storeItems.store.setError(error.error),
      });
  }

  setOrder(order: OrderListItemModel, storeKey: LocalStateKeys) {
    const store = this.orderItemsStoreService.getOrCreateStore(storeKey);
    applyTransaction(() => {
      this.clearCache(storeKey);
      store.store.add(order);
      this.setActiveById(order.orderID, storeKey);
    });
  }

  /**
   * Set Page which is loaded.
   */
  setPage(nextPage: number, storeKey: LocalStateKeys) {
    const store = this.orderItemsStoreService.getOrCreateStore(storeKey);
    store.store.update((state) => ({
      ...state,
      page: nextPage,
    }));
  }

  /**
   * Initialize State.
   */
  clearCache(storeKey: LocalStateKeys) {
    const store = this.orderItemsStoreService.getOrCreateStore(storeKey);
    store.store.update((state) => ({
      ...state,
      page: 0,
      currentPage: 0,
      pageSize: 50,
      lastPage: false,
    }));
    store.store.remove();
  }

  setActiveById(id: number, storeKey: LocalStateKeys) {
    const store = this.orderItemsStoreService.getOrCreateStore(storeKey);
    store.store.setActive(id);
  }

  changeSearchFilterValue(
    filter: string,
    storeConfig?: LocalStoreConfigModel
  ): void {
    this.filterValueChange(filter, storeConfig.orderItems);
  }

  initializeStore(storeConfig?: LocalStoreConfigModel): void {
    this.resetStore(storeConfig);
    this.getNextPage(storeConfig.orderItems);
  }

  onSort(
    sortKey: string,
    sortDirection: string,
    storeConfig?: LocalStoreConfigModel
  ): void {}

  resetStore(storeConfig?: LocalStoreConfigModel): void {
    this.clearCache(storeConfig.orderItems);
  }

  setActive(
    item: OrderListItemModel,
    storeConfig?: LocalStoreConfigModel
  ): void {
    this.setActiveById(item.orderID, storeConfig.orderItems);
  }

  calcAKSPlan(orderID: number, storeKey: LocalStateKeys): Observable<boolean> {
    const store = this.orderItemsStoreService.getOrCreateStore(storeKey);
    store.store.setLoading(true);

    return this.orderService.calculateAksPlan(orderID).pipe(
      tap(() => store.store.setLoading(false)),
      map((data: TaskResult) => data.successful)
    );
  }
}
